import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  Text
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useJoinWebMeetingMutation } from "@store/api/meeting";
import { showErrorToast } from "@store/customActions";
import { $Routes } from "@routes";

const WebMeetingCodeEntry = ({ isOpen, onClose }) => {
  const [meetingCode, setMeetingCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [joinWebMeeting] = useJoinWebMeetingMutation();

  const handleSubmit = async () => {
    // Validate meeting code format (6 characters)
    if (!meetingCode || meetingCode.length !== 6) {
      setError("Please enter a valid 6-character meeting code");
      return;
    }

    setIsSubmitting(true);
    setError("");
    
    try {
      // Make API call to join the meeting
      await joinWebMeeting(meetingCode.toUpperCase()).unwrap();
      
      // On success, navigate to the root page and close modal
      navigate($Routes.rootPage);
      handleClose();
    } catch (error) {
      // Handle error - display in modal
      const errorMessage = error?.data?.message || "Failed to join meeting";
      setError(errorMessage);
      setIsSubmitting(false);
      
      // Log error for debugging
      console.error('Failed to join meeting:', error);
    }
  };

  const handleClose = () => {
    setMeetingCode("");
    setError("");
    setIsSubmitting(false);
    onClose();
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    // Only allow alphanumeric characters and limit to 6
    if (/^[A-Z0-9]*$/.test(value) && value.length <= 6) {
      setMeetingCode(value);
      setError("");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Join Meeting</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Enter the meeting code provided by your host
            </Text>
            
            <FormControl isInvalid={!!error}>
              <FormLabel>Meeting Code</FormLabel>
              <Input
                placeholder="Enter 6-character code"
                value={meetingCode}
                onChange={handleCodeChange}
                size="lg"
                textAlign="center"
                fontSize="xl"
                fontWeight="bold"
                letterSpacing="wider"
                textTransform="uppercase"
                maxLength={6}
              />
              <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={meetingCode.length !== 6}
            data-testid="join-web-meeting-button"
          >
            Join Meeting
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WebMeetingCodeEntry;